/**
 * What a blocked Environment Service says over HTTP while it refuses to start.
 *
 * The service stays up precisely so it can answer the question "why". These
 * schemas are the wire form of that answer: the server builds the body from
 * them, and Local Launch Control decodes it back into the state its
 * presentation authority already knows how to describe.
 */
import * as Option from "effect/Option";
import * as Schema from "effect/Schema";

export const HOST_STORE_LOCKOUT_ERROR = "host_store_unavailable" as const;

/**
 * Which property of a Host Store target refused, from a closed set. Named here
 * because three surfaces read it off the wire and one of them — the Environment
 * Service CLI — has no room for the refusing service's own sentence and must
 * pick its own from this vocabulary (#957).
 */
export const HostStoreTargetRefusalWire = Schema.Literals([
  "unsafe-path-shape",
  "unexpected-owner",
  "unexpected-mode",
  "unexpected-acl",
  "outside-host-store",
]);
export type HostStoreTargetRefusalWire = typeof HostStoreTargetRefusalWire.Type;

export const HostStoreBlockedAccessError = Schema.Struct({
  mode: Schema.Literal("access-error"),
  /** What the service was doing, phrased to complete "AFAR could not ...". */
  operation: Schema.String,
  failedPath: Schema.String,
  storePath: Schema.String,
  /**
   * Which property of the target was wrong, when the refusing check could name
   * one. It is what lets a surface say what to restore, and it is deliberately
   * a closed vocabulary rather than the check's own sentence: that sentence is
   * built from whatever threw and may carry host detail this never should.
   * Absent from a service older than the vocabulary, and from an I/O failure
   * that refused before reaching any property, so every reader tolerates that.
   */
  refusal: Schema.optional(HostStoreTargetRefusalWire),
});

export const HostStoreBlockedInvalidApplicationInstallation = Schema.Struct({
  mode: Schema.Literal("invalid-application-installation"),
  /** Why this installation was refused, in the refusing check's own words. */
  detail: Schema.optional(Schema.String),
  packagePath: Schema.optional(Schema.String),
});

export const HostStoreBlockedStateWire = Schema.Union([
  HostStoreBlockedAccessError,
  HostStoreBlockedInvalidApplicationInstallation,
]);
export type HostStoreBlockedStateWire = typeof HostStoreBlockedStateWire.Type;

/**
 * The `503` a machine client receives from a locked-out service. `blocked` is
 * absent on the unauthenticated machine paths and on services older than the
 * explanation, so every reader has to tolerate its absence.
 */
export const HostStoreLockoutBody = Schema.Struct({
  error: Schema.Literal(HOST_STORE_LOCKOUT_ERROR),
  /**
   * Deliberately open: a lifecycle state this reader has never heard of must
   * still decode, or an old client loses the explanation along with the mode.
   */
  mode: Schema.String,
  message: Schema.String,
  blocked: Schema.optional(HostStoreBlockedStateWire),
});
export type HostStoreLockoutBody = typeof HostStoreLockoutBody.Type;

const decodeLockoutBody = Schema.decodeUnknownOption(HostStoreLockoutBody);

/**
 * A body that is missing, truncated, or not this shape is not an error worth
 * raising: the caller still has the status it came with.
 */
export const decodeHostStoreLockoutBody = (value: unknown): HostStoreLockoutBody | undefined =>
  Option.getOrUndefined(decodeLockoutBody(value));
